import * as React from 'react';
import { ReactElement, useState } from 'react';

import { get } from '../../api/utils';
import { pluralize } from '../../utils';
import ErrorAlert from '../common/ErrorAlert';
import Icon from '../common/Icon';
import Modal from '../common/modal/Modal';
import Spinner from '../common/Spinner';

interface ICurriculumReference {
    id: number;
    name: string;
    uri: string;
}

interface IProps {
    curriculumsCount: number;
    inline?: boolean;
    metadata?: boolean;
    projectId: number;
}

export default function CurriculumItemableCurriculumsLink({
    curriculumsCount,
    inline = false,
    metadata = false,
    projectId,
}: IProps): ReactElement {
    const modalId = `curriculums-modal-${projectId}`;
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [curriculums, setCurriculums] = useState<ICurriculumReference[]>(
        [],
    );
    const [loaded, setLoaded] = useState<boolean>(false);

    const onLoadCurriculums = async (): Promise<void> => {
        if (loaded) {
            return;
        }

        setError(null);
        setLoading(true);

        try {
            const response = await get<ICurriculumReference[]>(
                `/projects/${projectId}/curriculums.json`,
            );
            setCurriculums(response);
            setLoaded(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const linkStyle = metadata
        ? { color: 'inherit', cursor: 'pointer', fontSize: 'inherit' }
        : inline
          ? { cursor: 'pointer' }
          : undefined;

    const link = (
        <a
            className={metadata || inline ? 'td-none' : 'text-muted td-none'}
            data-target={`#${modalId}`}
            data-toggle="modal"
            href={`#${modalId}`}
            onClick={(): void => {
                void onLoadCurriculums();
            }}
            style={linkStyle}
            title="Show curriculums"
        >
            <Icon
                fixedWidth={metadata}
                name="graduation-cap"
                text={metadata ? undefined : `Used in ${pluralize(curriculumsCount, 'curriculum')}`}
            />
            {metadata && `Used in ${pluralize(curriculumsCount, 'curriculum')}`}
        </a>
    );

    return (
        <>
            {inline || metadata ? (
                link
            ) : (
                <small className="align-items-center d-flex gap-2 text-muted">
                    {link}
                </small>
            )}

            <Modal
                id={modalId}
                title={`Used in ${pluralize(curriculumsCount, 'curriculum')}`}
            >
                {loading && <Spinner />}

                {!loading && error && <ErrorAlert error={error} />}

                {!loading && !error && curriculums.length > 0 && (
                    <ul className="list-unstyled mb-0">
                        {curriculums.map((curriculum) => (
                            <li className="mb-2" key={curriculum.id}>
                                <a href={curriculum.uri}>
                                    #{curriculum.id} {curriculum.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                )}

                {!loading && !error && curriculums.length === 0 && loaded && (
                    <p className="mb-0 text-muted">
                        No curriculums found for this project.
                    </p>
                )}
            </Modal>
        </>
    );
}
